import { ThemeConfig } from 'antd';
import { designTokens } from './design-tokens';

const DEFAULT_PRIMARY_COLOR = designTokens.primary; // Venn default

/**
 * App Package ID constants for each brand/app.
 */
export const AppPackageId = {
  VENN: 'com.venn',
  MAC: 'com.venn.mac',
  ASPIRE: 'com.venn.aspire',
  ALGONQUIN: 'com.venn.algonquin',
  REGENTS_PARK: 'com.venn.regentspark',
  BOZZUTO: 'com.venn.bozzuto',
  FERNWOOD: 'com.venn.fernwood',
  THE_DIME: 'com.venn.thedime',
  PERENNIAL_CORKTOWN: 'com.venn.perennialcorktown',
  GOWANUS_WHARF: 'com.venn.gowanuswharf',
  COMMUNITY_REALTY: 'com.venn.communityrealty',
  WEST_HOUSE_TORONTO: 'com.venn.westhousetoronto',
  RELATED_LIFE: 'com.venn.relatedlife',
  CIM: 'com.venn.cim',
  VERIS: 'com.venn.veris',
  CASA_MEU: 'com.venn.casameu',
  BERKSHIRE: 'com.venn.berkshire',
  HALO: 'com.venn.halo',
  ONEWALL: 'com.venn.onewall',
  STEAD: 'com.venn.stead',
  GOTHAM: 'com.venn.gotham',
  DERMOT: 'com.venn.dermot',
} as const;

export type AppPackageIdType = (typeof AppPackageId)[keyof typeof AppPackageId];

/**
 * Mapping from appPackageId to primary color.
 */
const appPackageIdColors: Record<AppPackageIdType, string> = {
  [AppPackageId.VENN]: '#EA7555',
  [AppPackageId.MAC]: '#00ACEF',
  [AppPackageId.ASPIRE]: '#0083B5',
  [AppPackageId.ALGONQUIN]: '#FB7400',
  [AppPackageId.REGENTS_PARK]: '#58B4EB',
  [AppPackageId.BOZZUTO]: '#006747',
  [AppPackageId.FERNWOOD]: '#84A783',
  [AppPackageId.THE_DIME]: '#1E1218',
  [AppPackageId.PERENNIAL_CORKTOWN]: '#AF6431',
  [AppPackageId.GOWANUS_WHARF]: '#3471A7',
  [AppPackageId.COMMUNITY_REALTY]: '#5F99BC',
  [AppPackageId.WEST_HOUSE_TORONTO]: '#000000',
  [AppPackageId.RELATED_LIFE]: '#866E4B',
  [AppPackageId.CIM]: '#00677F',
  [AppPackageId.VERIS]: '#444A40',
  [AppPackageId.CASA_MEU]: '#613A3B',
  [AppPackageId.BERKSHIRE]: '#003D88',
  [AppPackageId.HALO]: '#1C2B39',
  [AppPackageId.ONEWALL]: '#0B173B',
  [AppPackageId.STEAD]: '#6B5C73',
  [AppPackageId.GOTHAM]: '#131516',
  [AppPackageId.DERMOT]: '#0B0C11',
};

export function generateAntTheme(appPackageId: string = '') {
  const primaryColor = appPackageIdColors[appPackageId as AppPackageIdType] || DEFAULT_PRIMARY_COLOR;

  return generateAntThemeByPrimaryColor(primaryColor);
}

export function generateAntThemeByPrimaryColor(primaryColor: string = DEFAULT_PRIMARY_COLOR): ThemeConfig {
  return {
    token: {
      fontFamily: designTokens.fontFamily,
      colorPrimary: primaryColor,
      colorInfo: designTokens.info,
      colorPrimaryBg: designTokens.primaryBg,
      colorPrimaryBgHover: designTokens.primaryBgHover,
      colorPrimaryHover: designTokens.primaryHover,
      colorPrimaryActive: designTokens.primaryActive,
      colorPrimaryTextHover: designTokens.primaryTextHover,
      colorPrimaryTextActive: designTokens.primaryTextActive,
      colorSuccess: designTokens.success,
      colorWarning: designTokens.warning,
      colorError: designTokens.error,
      colorLink: designTokens.link,
      colorTextBase: designTokens.textBase,
      colorTextSecondary: designTokens.textSecondary,
      colorTextTertiary: designTokens.textTertiary,
      colorTextQuaternary: designTokens.textQuaternary,
      colorBorder: designTokens.border,
      colorBorderSecondary: designTokens.borderSecondary,
      borderRadiusLG: 8,
      borderRadius: 6,
      borderRadiusXS: 2,
      borderRadiusSM: 4,
      boxShadow: '',
      colorPrimaryBorder: designTokens.primaryBorder,
      colorPrimaryBorderHover: designTokens.primaryBorderHover,
      colorPrimaryText: designTokens.primaryText,
      colorSuccessBg: designTokens.successBg,
      colorSuccessBgHover: designTokens.successBgHover,
      colorSuccessBorder: designTokens.successBorder,
      colorSuccessBorderHover: designTokens.successBorderHover,
      colorSuccessHover: designTokens.successHover,
      colorSuccessActive: designTokens.successActive,
      colorSuccessText: designTokens.successText,
      colorSuccessTextHover: designTokens.successTextHover,
      colorSuccessTextActive: designTokens.successTextActive,
      colorText: designTokens.text,
      fontSizeHeading1: designTokens.fontSizeHeading1,
      fontSizeHeading2: designTokens.fontSizeHeading2,
      fontSizeHeading3: designTokens.fontSizeHeading3,
      fontSizeHeading4: designTokens.fontSizeHeading4,
      fontSizeHeading5: designTokens.fontSizeHeading5,
      colorLinkHover: designTokens.linkHover,
      colorLinkActive: designTokens.linkActive,
      colorErrorBg: designTokens.errorBg,
      colorErrorBgHover: designTokens.errorBgHover,
      colorErrorBorder: designTokens.errorBorder,
      colorErrorBorderHover: designTokens.errorBorderHover,
      colorErrorHover: designTokens.errorHover,
      colorErrorActive: designTokens.errorActive,
      colorErrorTextHover: designTokens.errorTextHover,
      colorErrorText: designTokens.errorText,
      colorErrorTextActive: designTokens.errorTextActive,
      colorWarningBg: designTokens.warningBg,
      colorWarningBgHover: designTokens.warningBgHover,
      colorWarningBorder: designTokens.warningBorder,
      colorWarningBorderHover: designTokens.warningBorderHover,
      colorWarningHover: designTokens.warningHover,
      colorWarningActive: designTokens.warningActive,
      colorWarningTextHover: designTokens.warningTextHover,
      colorWarningText: designTokens.warningText,
      colorWarningTextActive: designTokens.warningTextActive,
      wireframe: false,
      colorInfoBg: designTokens.infoBg,
      colorInfoBgHover: designTokens.infoBgHover,
      colorInfoBorder: designTokens.infoBorder,
      colorInfoBorderHover: designTokens.infoBorderHover,
      colorInfoHover: designTokens.infoHover,
      colorInfoActive: designTokens.infoActive,
      colorInfoTextHover: designTokens.infoTextHover,
      colorInfoTextActive: designTokens.infoTextActive,
      colorBgSpotlight: designTokens.bgSpotlight,
    },
    components: {
      Alert: {
        withDescriptionPadding: '16px',
        borderRadiusLG: 12,
        withDescriptionIconSize: 21,
      },
      Tag: {
        defaultBg: '#3B3B3E0F',
        controlPaddingHorizontalSM: 8,
      },
      Tooltip: {
        borderRadius: 12,
        paddingSM: 12,
      },
      Button: {
        dangerShadow: '',
        defaultShadow: '',
        primaryShadow: '',
        primaryColor: 'rgb(255,255,255)',
        defaultGhostColor: 'rgb(59,59,62)',
        paddingInlineSM: '12px',
        paddingInlineLG: '16px',
        defaultHoverBg: designTokens.colorFillSecondary,
        defaultActiveBg: designTokens.colorFillContentHover,
        textTextActiveColor: designTokens.textBase,
        textTextHoverColor: designTokens.textBase,
        // A gentle, clearly-perceptible ease on hover/focus so buttons don't feel dead.
        motionDurationMid: '0.2s',
        opacityLoading: 1,
        borderRadiusLG: 8,
        borderRadius: 8,
        borderRadiusSM: 6,
      },
      Input: {
        activeShadow: '',
        errorActiveShadow: '',
        warningActiveShadow: '',
      },
      InputNumber: {
        activeShadow: '',
        errorActiveShadow: '',
        warningActiveShadow: '',
      },
      Mentions: {
        activeShadow: '',
        errorActiveShadow: '',
        warningActiveShadow: '',
      },
      DatePicker: {
        activeShadow: '',
        errorActiveShadow: '',
        warningActiveShadow: '',
      },
      Select: {
        colorWarningOutline: 'rgba(242,216,17,0)',
        colorErrorOutline: 'rgba(238,38,38,0)',
        activeOutlineColor: 'rgba(5,147,255,0)',
      },
      Table: {
        headerBg: '',
        // A clearly-visible warm-grey row hover (the antd default is near-invisible on white).
        rowHoverBg: designTokens.colorFillSecondary,
      },
      Collapse: {
        colorBorder: designTokens.borderSecondary,
        headerBg: 'transparent',
        contentBg: 'transparent',
      },
      List: {
        colorBorder: designTokens.borderSecondary,
      },
      Notification: {
        boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05);',
      },
    },
  };
}
